import React, { useState, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import api, { formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

export const NewsletterBlast = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/newsletter-subscribers').then((res) => setCount((res.data || []).length)).catch(() => setCount(0));
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Send this newsletter to ${count} subscriber${count === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/admin/newsletter/send', { subject, message });
      toast.success(data.message);
      setSubject('');
      setMessage('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-[#f1e3eb]/50 border border-[#5d6b3c]/15 mb-10" data-testid="newsletter-blast-panel">
      <CardContent className="p-6">
        <h3 className="font-serif text-lg text-[#3c4a2a] mb-1">Send a Newsletter</h3>
        <p className="text-sm text-[#5f6d51]/70 mb-5">
          Write one update and email it to {count === null ? 'all' : `all ${count}`} newsletter subscriber{count === 1 ? '' : 's'}.
        </p>
        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <Label htmlFor="nl-subject" className="text-[#3c4a2a]">Subject *</Label>
            <Input
              id="nl-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              maxLength={200}
              placeholder="e.g., September Kickoff Details"
              data-testid="newsletter-subject-input"
              className="mt-2 bg-white border-[#bd8cae]/50 focus:border-[#5d6b3c]"
            />
          </div>
          <div>
            <Label htmlFor="nl-message" className="text-[#3c4a2a]">Message *</Label>
            <Textarea
              id="nl-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={6}
              maxLength={10000}
              placeholder="Write your update — one paragraph per line."
              data-testid="newsletter-message-input"
              className="mt-2 bg-white border-[#bd8cae]/50 focus:border-[#5d6b3c]"
            />
          </div>
          <Button
            type="submit"
            disabled={sending || count === 0}
            data-testid="newsletter-send-btn"
            className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {count === 0 ? 'No subscribers yet' : `Send to ${count ?? '…'} subscriber${count === 1 ? '' : 's'}`}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
